<template>
  <DefaultLayout class="UserChartView">
    <h1 class="mb-4 display-1">
      События
      <template v-if="userId">
        пользователя
        {{ $store.getters['user/displayName'](userId) }}
      </template>
      <template v-else>
        Пользователей
      </template>
    </h1>
    <VAlert
      v-if="!authorized"
      border="left"
      outlined
      color="primary"
      icon="mdi-account-circle"
    >
      Для просмотра событий требуется
      <VBtn
        text
        small
        color="primary"
        @click="$store.commit('ui/setLoginModal', { value: true })"
      >
        авторизоваться
      </VBtn>
    </VAlert>
    <div
      v-if="authorized"
      class="chart-wrapper"
    >
      <ChartToolbar
        :disabled="loading"
        :user-ids.sync="ui.filters.userIds"
        :date.sync="ui.filters.date"
        :date-menu-is-open.sync="ui.dtMenuIsOpen"
        :grouping.sync="ui.options.grouping"
        :wide-view.sync="ui.options.wideView"
        :types-as-categories.sync="ui.options.typesAsCategories"
        :loaded-events="events && events.length"
        :total-events="eventsTotal"
        :load-more="moreLimit"
        :style="{ top: $vuetify.breakpoint.smAndDown ? '56px' : '64px' }"
        @click:get-more-events="getMoreEvents()"
      />
      <VAlert
        v-if="irrelevantUserPage"
        border="left"
        outlined
        color="primary"
        icon="mdi-account-alert"
      >
        Данный пользователь не является вашим подчиненным.
        Если вы хотите видеть события данного пользователя, пожалуйста,
        попросите администратора вашей компании добавить
        на вкладке "Настройки" &rarr; "Настройки пользователей"
        {{ $store.getters['user/displayName'](userId) }}
        в список ваших подчиненных.
      </VAlert>
      <template v-else>
        <VAlert
          v-if="events != null && events.length < eventsTotal"
          border="left"
          outlined
          class="mt-6 mx-2"
          :color="company && company.isPremium ? 'primary' : 'warning'"
          icon="mdi-cloud-alert"
        >
          <template v-if="company && company.isPremium">
            Показаны первые
            {{ events.length }}/{{ eventsTotal }} событий
            <VBtn
              text
              small
              color="secondary"
              :disabled="loading"
              @click="getMoreEvents()"
            >
              загрузить еще {{ moreLimit }}
            </VBtn>
          </template>
          <template v-else-if="company">
            В бесплатной версии отображается до {{ company.eventLimit }}
            событий на пользователя.
          </template>
        </VAlert>
        <EventChartSet
          :grouping.sync="ui.options.grouping"
          :wide-view.sync="ui.options.wideView"
          :types-as-categories.sync="ui.options.typesAsCategories"
          @reset-date="ui.dtMenuIsOpen = true"
          @reset-filters="resetFilters()"
        />
      </template>
    </div>
  </DefaultLayout>
</template>

<script>
import moment from 'moment'
import * as R from 'ramda'

import EventChartSet from '../components/EventChartSet'
import DefaultLayout from '../layouts/Default'
import ChartToolbar from '../components/ChartToolbar'

const ISO_DATE = 'YYYY-MM-DD'
const toIsoDate = dt => moment(dt).format(ISO_DATE)
const PRETTY_DATE = 'll'
const toPrettyDate = dt => moment(dt).format(PRETTY_DATE)

const defaultFilters = () => {
  const today = toIsoDate()
  return ({
    date: today,
    userIds: [],
    _preventDefault: false,
  })
}

export default {
  name: 'UserChart',
  components: {
    ChartToolbar,
    DefaultLayout,
    EventChartSet,
  },
  props: {
    userId: { type: Number, default: null },
    date: {
      type: String,
      default: null,
      validate: date => moment(date).isValid(),
    },
  },
  data() {
    return {
      ui: {
        // серверная фильтрация
        filters: defaultFilters(),
        options: {
          grouping: 'user',
          wideView: false,
          typesAsCategories: false,
        },
        dtMenuIsOpen: false,
      },
    }
  },
  computed: {
    currentUser() { return this.$store.state.user.current },
    allUsers() { return this.$store.getters['user/relevant'] },
    allUserIds() { return this.allUsers && R.map(R.prop('id'), this.allUsers) },
    authorized() { return !!this.$store.state.auth.token },
    events() { return this.$store.getters['event/events'] }, // События для чарта
    eventsTotal() { return this.$store.state.event.total }, // N событий на чарте
    loading() { return this.$store.state.event.loading }, // N событий на чарте
    limit() { return this.$store.state.event.MAX_RECORDS },
    moreLimit() { return this.$store.state.event.LOAD_MORE_RECORDS },
    company() { return this.$store.state.company.current },
    dateFilterTextDisplay() { return toPrettyDate(this.ui.filters.date) },
    defaultFilters() {
      const { currentUser: user, date } = this
      const filters = defaultFilters()
      if (user !== null) {
        filters.userIds.push(user.id)
      }
      if (date) {
        filters.date = date
      }
      return filters
    },
    datePropPretty() { return this.date && moment(this.date).format('ll') },
    irrelevantUserPage() {
      const { currentUser, userId } = this
      return !!userId &&
        currentUser &&
        !currentUser.isAdmin &&
        currentUser.id !== userId &&
        !currentUser.subordinates.includes(userId)
    },
  },
  watch: {
    authorized: {
      handler(isAuthorized, wasAuthorized) {
        if (isAuthorized && !wasAuthorized) { this.getEvents() }
      },
      immediate: true,
    },
    defaultFilters: {
      handler(defaultFilters) {
        const { ui } = this
        if (!ui.filters._preventDefault) {
          if (!this.userId) ui.filters.userIds = defaultFilters.userIds
          if (!this.date) ui.filters.date = defaultFilters.date
        }
      },
      immediate: true,
    },
    allUserIds: {
      handler(allUserIds) {
        // Удаляем левых пользователей из фильтра (например при перелогине)
        const { ui: { filters } } = this
        if (!allUserIds || !filters) return
        if (R.all(
          R.includes(R.__, allUserIds),
          filters.userIds
        )) return
        filters.userIds = R.filter(
          R.includes(R.__, allUserIds),
          filters.userIds,
        )
      },
      immediate: true,
    },
    'ui.filters': {
      handler() {
        if (this.authorized) this.getEvents()
        if (!this.userId) this.storageDump()
      },
      deep: true,
    },
    userId: {
      handler(userId) {
        if (userId) this.ui.filters.userIds = [userId]
      },
      immediate: true,
    },
    date: {
      handler(date) {
        if (date) this.ui.filters.date = date
      },
      immediate: true,
    },
    'ui.filters.date'(date) {
      const newRoute = R.mergeDeepRight(
        this.$route,
        date ? { query: { date } } : {}
      )
      if (!date && newRoute.query && newRoute.query.hasOwnProperty('date')) {
        delete newRoute.query.date
      }
      this.$router.replace(newRoute)
    },
  },
  created() {
    if (!this.userId) this.storageLoad()
  },
  methods: {
    getEvents() {
      const {
        $store,
        limit,
        ui: { filters },
      } = this
      const filter = {
        dtStart: moment(filters.date).toISOString(),
        dtEnd: moment(filters.date).endOf('day').toISOString(),
        userId: filters.userIds.join(','),
      }
      return $store.dispatch('event/getList', { filter, limit })
    },
    getMoreEvents() {
      const {
        $store,
        moreLimit: limit,
        events,
        company,
        ui: { filters },
      } = this
      if (!company.isPremium) return
      const filter = {
        dtStart: moment(filters.date).toISOString(),
        dtEnd: moment(filters.date).endOf('day').toISOString(),
        userId: filters.userIds.join(','),
      }
      $store.dispatch('event/getList', {
        filter,
        limit,
        offset: events.length,
      })
    },
    resetFilters() {
      this.ui.filters = this.defaultFilters
    },
    today() { return moment().format(ISO_DATE) },
    scrollLeft() {
      const { filters } = this.ui
      filters.date = toIsoDate(moment(filters.date).subtract(1, 'day'))
    },
    scrollRight() {
      const { filters } = this.ui
      filters.date = toIsoDate(moment(filters.date).add(1, 'day'))
    },
    storageDump() {
      localStorage.setItem(
        '_UserChart.filters.userIds',
        JSON.stringify(this.ui.filters.userIds),
      )
    },
    storageLoad() {
      /* fixme: temp - удаляем более не релевантное значение,
                чтобы не сбивало с толку */
      localStorage.removeItem('_UserChart.filters')

      let userIds = localStorage.getItem('_UserChart.filters.userIds')
      if (userIds) {
        try {
          userIds = JSON.parse(userIds)
        } catch (e) {
          console.error(e)
        }
      }
      if (userIds && userIds.length) {
        this.ui.filters = {
          ...this.ui.filters,
          userIds,
          _preventDefault: true,
        }
      }
    },
  },
}
</script>

<style lang="sass" scoped>
  .chart-wrapper
    position: relative
    margin-left: -8px
    width: calc(100% + 40px)
    .ChartToolbar
      position: sticky
      top: 64px // app bar height
      left: 0
      right: 0
      z-index: 2
</style>
